import {ImgIcon} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcon';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import _range from 'lodash/range';
import * as React from 'react';

const styles = require('@mintdata/mint-kit/impl/properties/components/editableList/EditableList.less');
const cx = classnames.bind(styles);

export interface IProps
{
    isValid?:boolean;
    from?:number;
    times:number;
    itemRenderer:(item:number | any) => JSX.Element;
    formRenderer:() => JSX.Element;
    onRemove:(index:number) => void;
    onAdd:() => void;
    onMove?:(from, to) => void;
    focusElement?:HTMLInputElement;
}

export interface IState
{
    isMouseDown:boolean;
    isDragging:boolean;
    draggableItemIndex:number;
    draggableItemWidth:number;
    draggableItemHeight:number;
    dragOverItemIndex:number;
    x:number;
    y:number;
}

export class EditableList extends React.Component<IProps, IState>
{
    private items:HTMLLIElement[] = [];

    public constructor(props:IProps)
    {
        super(props);
        this.state = {
            isMouseDown: false,
            isDragging: false,
            draggableItemIndex: null,
            draggableItemWidth: null,
            draggableItemHeight: null,
            dragOverItemIndex: null,
            x: null,
            y: null,
        };
    }

    @bind
    private _onPlaceholderMouseOver(index:number)
    {
        return (e:any) =>
        {
            if( this.state.isDragging )
            {
                this.setState({dragOverItemIndex: index});
            }

        };
    }

    @bind
    private _onPlaceholderMouseUp(e:any)
    {
        if( this.state.dragOverItemIndex < this.state.draggableItemIndex )
        {
            this.props.onMove(this.state.draggableItemIndex, this.state.dragOverItemIndex + 1);
        }
        else
        {
            this.props.onMove(this.state.draggableItemIndex, this.state.dragOverItemIndex);
        }

        this.setState({isDragging: false, isMouseDown: false});
    }

    @bind
    private _onKeyDown(e:any)
    {
        if( e.keyCode === 27 )
        {
            this.setState({isDragging: false, isMouseDown: false});
        }
    }

    @bind
    private _onMouseDown(index:number)
    {
        return (e:any) =>
        {
            this.setState({
                draggableItemIndex: index,
                draggableItemHeight: this.items[index].offsetHeight,
                draggableItemWidth: this.items[index].offsetWidth,
                isMouseDown: true
            });
        };

    }

    @bind
    private _onMouseUp(e:any)
    {
        this.setState({isDragging: false, isMouseDown: false});
    }

    @bind
    private _onMouseMove(e:any)
    {
        const state:any = {x: e.clientX, y: e.clientY};

        if( this.state.isMouseDown && !this.state.isDragging )
        {
            state.isDragging = true;
        }

        this.setState(state);
    }

    @bind
    private _onFormKeyDown(e:any)
    {
        if( e.keyCode !== 13 ) return;

        e.preventDefault();

        if( this.props.isValid )
        {
            this.props.onAdd();
            this.props.focusElement && this.props.focusElement.focus();
        }
    }

    public render():JSX.Element
    {
        const {
            itemRenderer, onRemove, onAdd, from = 0,
            times, formRenderer, isValid = true
        } = this.props;

        const {
            isDragging, draggableItemIndex, x, y,
            draggableItemHeight, draggableItemWidth,
            dragOverItemIndex,
        } = this.state;

        return (
            <div
                onMouseMove={this._onMouseMove}
                onMouseUp={this._onMouseUp}
                onKeyDown={this._onKeyDown}
            >

                <ul className={styles.base}>
                    <li key={-1} className={cx(styles.item, {isDragging})}>
                        {isDragging && (
                            <div
                                className={cx(styles.dropPlaceholder, {
                                    isFirst: true,
                                    isDragOver: dragOverItemIndex === -1
                                })}
                                onMouseOver={this._onPlaceholderMouseOver(-1)}
                                onMouseUp={this._onPlaceholderMouseUp}
                            />
                        )}
                    </li>
                    {_range(from, times).map((index) => (
                        <li
                            ref={(el) =>
                            {
                                this.items[index] = el;
                            }}
                            key={index}
                            className={cx(styles.item, {isDragging})}
                        >
                            {itemRenderer(index)}
                            <button className={styles.button} onClick={() => onRemove(index)}>
                                <ImgIcon icon={ImgIcons.remove} className={styles.removeIcon} />
                                <ImgIcon icon={ImgIcons.removeActive} className={styles.removeIconActive} />
                            </button>
                            <button
                                className={cx(styles.dragButton, {isDragging})}
                                onMouseDown={this._onMouseDown(index)}
                            >
                                <ImgIcon icon={ImgIcons.dnd} />
                            </button>
                            {isDragging && (
                                <div
                                    className={cx(styles.dropPlaceholder, {isDragOver: dragOverItemIndex === index})}
                                    onMouseOver={this._onPlaceholderMouseOver(index)}
                                    onMouseUp={this._onPlaceholderMouseUp}
                                />
                            )}
                        </li>
                    ))}
                    <li key="addNewItem" className={cx(styles.item)}>
                        <form className={styles.form} onKeyDown={this._onFormKeyDown}
                              onSubmit={event => event.preventDefault()}>
                            {formRenderer()}
                            <div className={styles.formBorder} />
                        </form>
                        <button
                            className={cx(styles.button, styles.removeButton, {isValid})}
                            onClick={onAdd}
                            disabled={!isValid}
                        >
                            <ImgIcon icon={ImgIcons.add} className={styles.addIcon} />
                            <ImgIcon icon={ImgIcons.addActive} className={styles.addIconActive} />
                        </button>
                    </li>
                </ul>
                {isDragging && (
                    <div
                        className={styles.draggingElementWrapper}
                        style={{
                            width: (draggableItemWidth + 16) + 'px',
                            height: draggableItemHeight + 'px',
                            left: (x - 8) + 'px',
                            top: (y - draggableItemHeight / 2) + 'px'
                        }}
                    >
                        <div className={styles.draggingElement}>
                            {itemRenderer(draggableItemIndex)}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

